import React, { useState } from 'react';
import { Upload, message, Button, Card, Space, Divider, Alert } from 'antd';
import { UploadOutlined, FileOutlined } from '@ant-design/icons';
import { 
  useUploadBonReceptionDBF, 
  useUploadBonReceptionDetailsDBF, 
  useUploadBothBonReceptionDBF 
} from '../../../hooks/achatReceptionHooks';

const BulkUploadBonReception = () => {
  const [bonreceptionFile, setBonreceptionFile] = useState(null);
  const [detailsFile, setDetailsFile] = useState(null);
  
  const { handleUpload: uploadBonReception, loading: loadingBonReception, error: errorBonReception, uploadResult: resultBonReception } = useUploadBonReceptionDBF();
  const { handleUpload: uploadDetails, loading: loadingDetails, error: errorDetails, uploadResult: resultDetails } = useUploadBonReceptionDetailsDBF();
  const { handleUpload: uploadBoth, loading: loadingBoth, error: errorBoth, uploadResult: resultBoth } = useUploadBothBonReceptionDBF();

  const handleUploadBonReception = async () => {
    if (!bonreceptionFile) {
      message.error('Please select a DBF file first!');
      return;
    }

    try {
      await uploadBonReception(bonreceptionFile);
      message.success('BonReception uploaded successfully!');
      setBonreceptionFile(null);
    } catch (err) {
      message.error(`Upload failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleUploadDetails = async () => {
    if (!detailsFile) {
      message.error('Please select a DBF file first!');
      return;
    }

    try {
      await uploadDetails(detailsFile);
      message.success('DetailBonReception uploaded successfully!');
      setDetailsFile(null);
    } catch (err) {
      message.error(`Upload failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleUploadBoth = async () => {
    if (!bonreceptionFile || !detailsFile) {
      message.error('Please select both DBF files!');
      return;
    }

    try {
      await uploadBoth(bonreceptionFile, detailsFile);
      message.success('Both files uploaded successfully!');
      setBonreceptionFile(null);
      setDetailsFile(null);
    } catch (err) {
      message.error(`Upload failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const bonreceptionProps = {
    onRemove: () => {
      setBonreceptionFile(null);
    },
    beforeUpload: (file) => {
      const fileName = file.name.toLowerCase();
      // Check if file has .dbf extension (case-insensitive)
      if (!fileName.endsWith('.dbf')) {
        message.error('Please upload a DBF file (.dbf or .DBF)!');
        return Upload.LIST_IGNORE;
      }
      setBonreceptionFile(file);
      return false;
    },
    fileList: bonreceptionFile ? [bonreceptionFile] : [],
    accept: '.dbf,.DBF',
  };

  const detailsProps = {
    onRemove: () => {
      setDetailsFile(null);
    },
    beforeUpload: (file) => {
      const fileName = file.name.toLowerCase();
      // Check if file has .dbf extension (case-insensitive)
      if (!fileName.endsWith('.dbf')) {
        message.error('Please upload a DBF file (.dbf or .DBF)!');
        return Upload.LIST_IGNORE;
      }
      setDetailsFile(file);
      return false;
    },
    fileList: detailsFile ? [detailsFile] : [],
    accept: '.dbf,.DBF',
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card title="Upload BonReception DBF Files" style={{ marginBottom: 24 }}>
        <Alert
          message="DBF File Upload Instructions"
          description={
            <div>
              <p><strong>Upload BonReception Header file (.dbf):</strong> Contains the main bon reception records (typically BONREC.dbf)</p>
              <p><strong>Upload BonReception Detail file (.dbf):</strong> Contains the detail/line items for bon receptions (typically DETBONREC.dbf)</p>
              <p><strong>Upload Both:</strong> Upload both files together (recommended)</p>
              <p style={{ marginTop: 8, color: '#52c41a' }}>
                <strong>Note:</strong> Any file name with .dbf or .DBF extension is accepted
              </p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Upload BONREC.dbf */}
          <Card size="small" title="1. Upload BonReception Headers (.dbf)" bordered>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Upload {...bonreceptionProps}>
                <Button icon={<UploadOutlined />} disabled={loadingBonReception || loadingBoth}>
                  Select DBF File (Headers)
                </Button>
              </Upload>
              {bonreceptionFile && (
                <div style={{ marginTop: 8 }}>
                  <FileOutlined /> {bonreceptionFile.name}
                </div>
              )}
              <Button
                type="primary"
                onClick={handleUploadBonReception}
                disabled={!bonreceptionFile || loadingBonReception || loadingBoth}
                loading={loadingBonReception}
              >
                {loadingBonReception ? 'Uploading...' : 'Upload BonReception'}
              </Button>
              {errorBonReception && (
                <Alert message={errorBonReception} type="error" showIcon style={{ marginTop: 8 }} />
              )}
              {resultBonReception && (
                <Alert
                  message="Upload Successful"
                  description={resultBonReception.message || 'BonReception seeded successfully'}
                  type="success"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </Space>
          </Card>

          {/* Upload DETBONREC.dbf */}
          <Card size="small" title="2. Upload BonReception Details (.dbf)" bordered>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Upload {...detailsProps}>
                <Button icon={<UploadOutlined />} disabled={loadingDetails || loadingBoth}>
                  Select DBF File (Details)
                </Button>
              </Upload>
              {detailsFile && (
                <div style={{ marginTop: 8 }}>
                  <FileOutlined /> {detailsFile.name}
                </div>
              )}
              <Button
                type="primary"
                onClick={handleUploadDetails}
                disabled={!detailsFile || loadingDetails || loadingBoth}
                loading={loadingDetails}
              >
                {loadingDetails ? 'Uploading...' : 'Upload Details'}
              </Button>
              {errorDetails && (
                <Alert message={errorDetails} type="error" showIcon style={{ marginTop: 8 }} />
              )}
              {resultDetails && (
                <Alert
                  message="Upload Successful"
                  description={resultDetails.message || 'DetailBonReception seeded successfully'}
                  type="success"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </Space>
          </Card>

          <Divider>OR</Divider>

          {/* Upload Both */}
          <Card size="small" title="3. Upload Both Files Together (Recommended)" bordered>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Upload {...bonreceptionProps}>
                  <Button icon={<UploadOutlined />} disabled={loadingBoth}>
                    Select Headers File (.dbf)
                  </Button>
                </Upload>
                {bonreceptionFile && (
                  <span style={{ marginLeft: 8 }}>
                    <FileOutlined /> {bonreceptionFile.name}
                  </span>
                )}
              </div>
              <div>
                <Upload {...detailsProps}>
                  <Button icon={<UploadOutlined />} disabled={loadingBoth}>
                    Select Details File (.dbf)
                  </Button>
                </Upload>
                {detailsFile && (
                  <span style={{ marginLeft: 8 }}>
                    <FileOutlined /> {detailsFile.name}
                  </span>
                )}
              </div>
              <Button
                type="primary"
                size="large"
                onClick={handleUploadBoth}
                disabled={!bonreceptionFile || !detailsFile || loadingBoth}
                loading={loadingBoth}
                style={{ marginTop: 16 }}
              >
                {loadingBoth ? 'Uploading Both Files...' : 'Upload Both Files'}
              </Button>
              {errorBoth && (
                <Alert message={errorBoth} type="error" showIcon style={{ marginTop: 8 }} />
              )}
              {resultBoth && (
                <Alert
                  message="Upload Successful"
                  description={resultBoth.message || 'Both files seeded successfully'}
                  type="success"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </Space>
          </Card>
        </Space>
      </Card>
    </div>
  );
};

export default BulkUploadBonReception;











